import api from './api';

export type AgencyType = 'CONSTRUCTION_GUIDANCE' | 'SAFETY_MANAGEMENT' | 'HEALTH_MANAGEMENT';

export type DataSyncStatus = 'SUCCESS' | 'FAILED' | 'PARTIAL';

export interface DesignatedInstitution {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  agencyType: AgencyType;
  designationOffice: string | null;
  serviceScope: string | null;
  sourceDataset: string;
  lastSyncedAt: string;
}

export interface SyncLogSummary {
  id: string;
  status: DataSyncStatus;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  itemCount: number;
  errorMessage: string | null;
}

export interface DesignatedInstitutionStat {
  count: number;
  lastSyncedAt: string | null;
  lastSync: SyncLogSummary | null;
  recentFailureCount: number;
}

export interface ScheduleInfo {
  cron: string;
  description: string;
  timezone: string;
  nextRunAt: string;
}

export interface DesignatedInstitutionStatsResponse {
  categories: {
    CONSTRUCTION_GUIDANCE: DesignatedInstitutionStat;
    SAFETY_MANAGEMENT: DesignatedInstitutionStat;
    HEALTH_MANAGEMENT: DesignatedInstitutionStat;
  };
  schedule: ScheduleInfo | null;
}

export interface SyncResult {
  type: AgencyType;
  count: number;
  error?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: null | { code: string; message: string };
  meta?: { page: number; limit: number; total: number };
}

interface ListResponse {
  data: DesignatedInstitution[];
  meta: { page: number; limit: number; total: number };
}

export const designatedInstitutionsApi = {
  async list(params: {
    type?: AgencyType;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ListResponse> {
    const query = new URLSearchParams();
    if (params.type) query.set('type', params.type);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));

    const qs = query.toString();
    const res = await api.get<ApiResponse<DesignatedInstitution[]>>(
      `/admin/designated-institutions${qs ? `?${qs}` : ''}`,
    );
    return {
      data: res.data.data,
      meta: res.data.meta ?? { page: 1, limit: 50, total: 0 },
    };
  },

  async getStats(): Promise<DesignatedInstitutionStatsResponse> {
    const res = await api.get<ApiResponse<DesignatedInstitutionStatsResponse>>(
      '/admin/designated-institutions/stats',
    );
    return res.data.data;
  },

  async syncAll(): Promise<SyncResult[]> {
    const res = await api.post<ApiResponse<SyncResult[]>>(
      '/admin/designated-institutions/sync',
    );
    return res.data.data;
  },

  async syncOne(type: AgencyType): Promise<SyncResult> {
    const res = await api.post<ApiResponse<SyncResult>>(
      `/admin/designated-institutions/sync/${type}`,
    );
    return res.data.data;
  },

};
